import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { pitchLabel } from "@/lib/pitchLabel";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ArrowLeftRight, Filter, MessageSquare, Users, X, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerUK } from "@/components/app/DatePickerUK";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

export default function SwapBoard() {
  const { user } = useAuth();
  const isCoachy = user?.role === "admin" || user?.role === "coach";

  const [board, setBoard] = useState({ count: 0, listings: [], facets: { age_groups: [], team_colors: [] } });
  const [loading, setLoading] = useState(true);
  const [ageGroup, setAgeGroup] = useState("all");
  const [teamColor, setTeamColor] = useState("all");
  const [sessionType, setSessionType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // "Offer my slot" flow
  const [offerFor, setOfferFor] = useState(null); // listing booking to swap INTO
  const [myBookings, setMyBookings] = useState([]);
  const [chosenSource, setChosenSource] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!isCoachy) return;
    setLoading(true);
    try {
      const params = {};
      if (ageGroup !== "all") params.age_group = ageGroup;
      if (teamColor !== "all") params.team_color = teamColor;
      if (sessionType !== "all") params.session_type = sessionType;
      if (dateFrom) params.from_date = dateFrom;
      if (dateTo) params.to_date = dateTo;
      const { data } = await api.get("/swap-board", { params });
      setBoard(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load the Swap Board");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ageGroup, teamColor, sessionType, dateFrom, dateTo]);

  const activeFilterCount = [ageGroup !== "all", teamColor !== "all", sessionType !== "all", !!dateFrom, !!dateTo].filter(Boolean).length;
  const clearFilters = () => { setAgeGroup("all"); setTeamColor("all"); setSessionType("all"); setDateFrom(""); setDateTo(""); };

  const openOfferDialog = async (listing) => {
    setOfferFor(listing);
    setChosenSource("");
    setNote("");
    // Load caller's own upcoming confirmed non-series bookings so they can pick one to offer.
    // Backend enforces "same day OR next-team-fixture" — same-day is by far the common case,
    // so we filter client-side to same-day matches to avoid guaranteed-failure clicks.
    try {
      const [{ data: mine }, { data: swaps }] = await Promise.all([
        api.get("/bookings/mine"),
        api.get("/booking-swaps"),
      ]);
      const pendingIds = new Set();
      (swaps.outgoing || []).forEach(s => pendingIds.add(s.initiator_booking_id));
      (swaps.incoming || []).forEach(s => pendingIds.add(s.target_booking_id));
      const today = new Date().toISOString().slice(0, 10);
      const eligible = (mine || []).filter(b =>
        b.status === "confirmed" &&
        b.booking_date >= today &&
        !b.recurring_series_id &&
        b.id !== listing.id &&
        b.user_id === user.id &&
        b.booking_date === listing.booking_date &&
        !pendingIds.has(b.id)
      );
      setMyBookings(eligible);
      if (eligible.length === 0) {
        toast.info(`You've got no same-day bookings on ${listing.booking_date} to offer for swap.`);
      }
    } catch (err) {
      toast.error("Couldn't load your bookings");
      setOfferFor(null);
    }
  };

  const submitOffer = async () => {
    if (!offerFor || !chosenSource) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${chosenSource}/swap-request`, {
        target_booking_id: offerFor.id,
        message: note,
      });
      toast.success("Swap requested — target coach + admins have been notified");
      setOfferFor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send that swap request");
    } finally { setBusy(false); }
  };

  const unlistOwn = async (id) => {
    if (!window.confirm("Remove your booking from the Swap Board?")) return;
    try {
      await api.post(`/bookings/${id}/swap-unlist`);
      toast.success("Removed from Swap Board");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't unlist that booking");
    }
  };

  const listings = board.listings || [];
  const ownCount = useMemo(() => listings.filter(b => b.is_own).length, [listings]);

  if (!isCoachy) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-3">Swap Board</div>
        <div className="opacity-70">Only coaches and admins can browse the Swap Board.</div>
      </div>
    );
  }

  return (
    <div data-testid="swap-board-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Swap-friendly slots</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Swap Board</h1>
          <div className="text-sm opacity-70 mt-2 max-w-xl">
            Bookings coaches have flagged as "happy to swap". Tap one, offer one of your own slots and both coaches (plus admins) get the request.
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-display font-black text-[#83B68B]" data-testid="swap-board-count">{board.count}</div>
          <div className="text-[10px] tracking-widest uppercase opacity-60">open{ownCount ? ` · ${ownCount} yours` : ""}</div>
        </div>
      </div>

      <div className="card-tactical rounded-md p-5 mb-8" data-testid="swap-board-filters">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-2">
            <Filter className="w-3 h-3" /> Filters {activeFilterCount > 0 && <span className="text-[#83B68B]">· {activeFilterCount} active</span>}
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} data-testid="swap-board-clear-filters" className="text-[10px] tracking-widest uppercase opacity-60 hover:opacity-100 hover:text-red-400">Clear all</button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Session</Label>
            <Select value={sessionType} onValueChange={setSessionType}>
              <SelectTrigger className="mt-1.5" data-testid="swap-board-session"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="game">Game</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Age group</Label>
            <Select value={ageGroup} onValueChange={setAgeGroup}>
              <SelectTrigger className="mt-1.5" data-testid="swap-board-age-group"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {board.facets.age_groups.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Kit colour</Label>
            <Select value={teamColor} onValueChange={setTeamColor}>
              <SelectTrigger className="mt-1.5" data-testid="swap-board-kit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {board.facets.team_colors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">From</Label>
            <div className="mt-1.5"><DatePickerUK value={dateFrom} onChange={setDateFrom} testId="swap-board-date-from" /></div>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">To</Label>
            <div className="mt-1.5"><DatePickerUK value={dateTo} onChange={setDateTo} testId="swap-board-date-to" /></div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="opacity-60 font-display tracking-widest">LOADING…</div>
      ) : listings.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center" data-testid="swap-board-empty">
          <Users className="w-8 h-8 opacity-40 mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Nothing on the board yet</div>
          <div className="text-sm opacity-70 mt-1">When a coach flags a booking as "happy to swap", it'll appear here.</div>
          <div className="text-xs opacity-50 mt-3">Go to My Bookings, hit "List for swap" on any upcoming slot to add yours.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="swap-board-list">
          {listings.map(b => (
            <div
              key={b.id}
              data-testid={`swap-board-card-${b.id}`}
              className={`card-tactical rounded-md p-5 flex flex-col gap-3 ${b.is_own ? "border-[#83B68B]/60 bg-[#83B68B]/5" : ""}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="font-display font-black text-2xl text-[#83B68B] leading-none">{format(parseISO(b.booking_date), "d MMM")}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mt-1">{format(parseISO(b.booking_date), "EEEE")}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-lg">{String(b.start_hour).padStart(2,"0")}:{String(b.start_min||0).padStart(2,"0")}</div>
                  <div className="text-[10px] tracking-widest uppercase opacity-50">→ {String(b.end_hour).padStart(2,"0")}:{String(b.end_min||0).padStart(2,"0")}</div>
                </div>
              </div>
              <div>
                <div className="font-display font-bold text-lg">{pitchLabel(b)}</div>
                <div className="text-xs opacity-70 mt-0.5">
                  {b.team_name || "no team"}{b.age_group ? ` · ${b.age_group}` : ""}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded ${b.session_type === "game" ? "bg-[#83B68B]/20 text-[#83B68B]" : "bg-muted opacity-80"}`}>
                    {b.session_type || "training"}{b.match_type ? ` · ${b.match_type}` : ""}
                  </span>
                  {b.team_color && (
                    <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-border">
                      Kit: {b.team_color}
                    </span>
                  )}
                  {b.opponent && <span className="text-xs opacity-70">vs {b.opponent}</span>}
                </div>
              </div>
              {b.swap_open_note && (
                <div className="text-xs opacity-80 border-l-2 border-[#83B68B]/60 pl-3 flex items-start gap-1.5" data-testid={`swap-board-note-${b.id}`}>
                  <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />
                  <span className="italic">"{b.swap_open_note}"</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                <div className="text-[10px] opacity-60 min-w-0 truncate">
                  Listed by {b.swap_opened_by_name || b.user_name}
                  {b.is_own && <span className="ml-1 text-[#83B68B] font-semibold">(you)</span>}
                </div>
                {b.is_own ? (
                  <button
                    onClick={()=>unlistOwn(b.id)}
                    data-testid={`swap-board-unlist-${b.id}`}
                    className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-red-500/60 hover:text-red-400 inline-flex items-center gap-1.5"
                  >
                    <X className="w-3 h-3" /> Unlist
                  </button>
                ) : b.has_pending_swap ? (
                  <span data-testid={`swap-board-pending-${b.id}`} className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-amber-400/40 bg-amber-400/10 text-amber-400 inline-flex items-center gap-1.5">
                    Swap pending
                  </span>
                ) : (
                  <button
                    onClick={()=>openOfferDialog(b)}
                    data-testid={`swap-board-offer-${b.id}`}
                    className="btn-volt text-[10px] tracking-widest uppercase px-3 py-1.5 rounded inline-flex items-center gap-1.5"
                  >
                    <ArrowLeftRight className="w-3 h-3" /> Offer my slot
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!offerFor} onOpenChange={(v)=>{ if (!v) setOfferFor(null); }}>
        <DialogContent className="max-w-lg" data-testid="swap-board-offer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl inline-flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#83B68B]" /> Offer a swap
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              Pick one of your upcoming bookings to offer in exchange. Both coaches + admins will be notified — first accept completes the swap.
            </DialogDescription>
          </DialogHeader>
          {offerFor && (
            <div className="space-y-4">
              <div className="rounded-md border border-[#83B68B]/40 bg-[#83B68B]/5 p-3 text-sm" data-testid="swap-board-target-summary">
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1">You'd get</div>
                <div className="font-medium">{format(parseISO(offerFor.booking_date), "EEE d MMM")} · {String(offerFor.start_hour).padStart(2,"0")}:{String(offerFor.start_min||0).padStart(2,"0")} · {pitchLabel(offerFor)}</div>
                <div className="opacity-70 text-xs">{offerFor.team_name || "no team"}{offerFor.opponent ? ` vs ${offerFor.opponent}` : ""}</div>
                {offerFor.swap_open_note && <div className="text-xs opacity-70 italic mt-1">"{offerFor.swap_open_note}"</div>}
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Offer one of your bookings</Label>
                {myBookings.length === 0 ? (
                  <div className="mt-2 rounded-md border border-border p-4 text-sm opacity-60 text-center">
                    No eligible upcoming bookings to offer. Create a single (non-series) confirmed booking first.
                  </div>
                ) : (
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto" data-testid="swap-board-offer-list">
                    {myBookings.map(t => {
                      const sameDay = t.booking_date === offerFor.booking_date;
                      return (
                        <label
                          key={t.id}
                          className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${chosenSource === t.id ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"}`}
                          data-testid={`swap-board-offer-source-${t.id}`}
                        >
                          <input
                            type="radio"
                            checked={chosenSource === t.id}
                            onChange={()=>setChosenSource(t.id)}
                            className="mt-1 accent-[#83B68B]"
                          />
                          <div className="flex-1 min-w-0 text-sm">
                            <div className="font-medium flex items-center gap-2">
                              {format(parseISO(t.booking_date), "EEE d MMM")} · {String(t.start_hour).padStart(2,"0")}:{String(t.start_min||0).padStart(2,"0")} · {pitchLabel(t)}
                              {sameDay && (
                                <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B] text-white inline-flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" /> Same day
                                </span>
                              )}
                            </div>
                            <div className="text-xs opacity-70 mt-0.5">
                              {t.team_name || "no team"}{t.opponent ? ` vs ${t.opponent}` : ""}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Optional note</Label>
                <textarea
                  value={note}
                  onChange={e=>setNote(e.target.value)}
                  maxLength={500}
                  rows={3}
                  data-testid="swap-board-offer-note"
                  className="w-full mt-2 bg-transparent border border-border rounded p-2 text-sm resize-none"
                  placeholder="Anything the other coach should know…"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={()=>setOfferFor(null)} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40">Cancel</button>
                <button
                  onClick={submitOffer}
                  disabled={busy || !chosenSource}
                  data-testid="swap-board-offer-submit"
                  className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeftRight className="w-4 h-4" /> {busy ? "Sending…" : "Send offer"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
